// Copyright © Orpyx® Medical Technologies Inc. 2025. All rights reserved.
// **ORPYX TRADE SECRET**
// **WARNING**: The contents of this repository, including source code and assets, are confidential. This source code and/or derived works are a trade secret of Orpyx Medical Technologies Inc. Unauthorized disclosure is strictly prohibited and may result in serious legal consequences.

import {Grid} from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import Calendar from "@material-ui/icons/CalendarToday"
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import {KeyboardDatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers"
import * as React from "react";
import {memo} from "react";
import {PatientInformationHeaderProps} from "./patient-information-header.interface";
import {usePatientInformationPageStyles} from "../patient-information-page/patient-information-page.style";
import {fullNamePipe} from "orpyx-web-common";
import moment from "moment";
import MomentUtils from "@date-io/moment"
import {ArrowDropDown} from "@material-ui/icons";
import {PatientPortalLink} from "../patient-portal-link/patient-portal-link";
import {PatientPriorityStatus} from "doctors-dashboard/http-clients/index";
import {statusLastChangedPipe} from "../../../../patients-list/components/patients-list-table-item/status-last-changed.pipe";

export const PatientInformationHeader = memo((props: PatientInformationHeaderProps) => {
    const classes = usePatientInformationPageStyles();

    const onFilterDateChange = (event: any) => {
        props.onFilterDateChange(event);
    }

    const contactPhoneNumber = props.patient.contactPhoneNumber;
    const hasPhoneNumber = contactPhoneNumber != null;

    const isPaused = props.patient.statusName === PatientPriorityStatus.Paused;
    const pausedStatusDetail = statusLastChangedPipe(props.patient.lastStatusUpdatedAt);

    // Formats phone number as (###) ###-####
    const formatPhoneNumber = (phoneNumber: string | null | undefined) => {
        if (phoneNumber == null) return null;
        return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`;
    }

    return (
        <>
        {isPaused && <div className={classes.pausedBanner} id="patient-information-paused-banner">
            This patient is currently paused. You may update their chart, but data ingestion for this patient is suspended.
            {pausedStatusDetail && ` Paused since ${pausedStatusDetail}.`}
        </div>}
        <div className={classes.header}>
            <Grid container className={classes.dashboardHeader} id="patient-information-header">

                <Grid item xs className={classes.horizontalLayout}>

                    <span className={classes.verticalLayout}>

                        <PersonIcon className={classes.icon} id="patient-information-header-patient-name-icon"/>

                        <span className={classes.patientDetailsLineItem}>
                            <span className={classes.patientDetailsLineItemLabel}>Patient Name:</span>
                            <span className={classes.patientDetailsLineItemValue}>{fullNamePipe(props.patient)}</span> 
                        </span>

                        {hasPhoneNumber && <span className={classes.patientDetailsLineItem}>
                            <span className={classes.patientDetailsLineItemLabel}>Contact Phone:</span>
                            <span className={classes.patientDetailsLineItemValue}>{formatPhoneNumber(contactPhoneNumber)}</span> 
                        </span>}

                    </span>

                </Grid>
                <Grid item xs="auto" className={`${classes.verticalLayout} ${classes.rightAlign}`}>

                        <AssignmentIndIcon className={classes.icon} id="patient-information-header-patient-id-icon"/>

                        <span className={classes.patientDetailsLineItem}>
                            <span className={classes.patientDetailsLineItemLabel}>Clinic:</span>
                            <span className={classes.patientDetailsLineItemValue}>{props.patient.organizationName}</span> 
                        </span>

                        <span className={classes.patientDetailsLineItem}>
                            <span className={classes.patientDetailsLineItemLabel}>Patient ID:</span>
                            <span className={classes.patientDetailsLineItemValue}>{props.patient.clientPatientIdentifier}</span> 
                        </span>

                        <span className={classes.patientDetailsLineItem}>
                            <span className={classes.patientDetailsLineItemLabel}>Orpyx ID:</span>
                            <span className={classes.patientDetailsLineItemValue}>{props.patient.patientId}</span> 
                        </span>

                        <PatientPortalLink patient={props.patient}/>

                </Grid>
                <Grid item xs className={classes.horizontalLayout}>
                    <span className={classes.verticalLayout}>
                        <Calendar className={classes.icon} id="patient-information-header-patient-calendar-icon" />
                        <span className={classes.calendarPicker}>
                        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                            <KeyboardDatePicker                           
                                    autoOk
                                    variant="inline"
                                    inputVariant="outlined"
                                    label="Month"
                                    format="MMM yyyy"
                                    onChange={onFilterDateChange}
                                    value={props.monitoringCycle.begin}
                                    disableFuture={true}
                                    openTo="month"
                                    minDate={props.patient.allowAccessStartDate?.startOf("month")}
                                    views={["year", "month"]}
                                    keyboardIcon={<ArrowDropDown style={{color: 'white' }} />}
                                    KeyboardButtonProps={{size: "medium"}}
                                    className = {classes.datePicker}
                                    InputProps={{ readOnly: true,
                                        classes: {
                                            input: classes.datePickerInput, 
                                            notchedOutline: classes.datePickerNotchedOutline } }}
                                    InputLabelProps={{ focused: false,
                                        classes: { 
                                            root: classes.datePickerLabel } }}
                                />
                        </MuiPickersUtilsProvider>
                    </span>
                    </span>

                </Grid>





            </Grid>
        </div>
        </>
    );
});